'use client';
import React from 'react';
import { Plus, Clock } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { Appointment } from './schedulingTypes';

interface Props {
  weekDates: Date[];
  appointments: Appointment[];
  days: string[];
  formatDateKey: (d: Date) => string;
  onCreateForDate: (dateStr: string) => void;
}

const statusBadge: Record<string, 'active' | 'pending' | 'info' | 'inactive' | 'warning'> = {
  scheduled: 'info',
  confirmed: 'active',
  'in-progress': 'warning',
  completed: 'active',
  cancelled: 'inactive',
  rescheduled: 'warning',
};

export default function WeeklyCalendarGrid({ weekDates, appointments, days, formatDateKey, onCreateForDate }: Props) {
  const today = formatDateKey(new Date('2026-08-13'));

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-border">
        {weekDates.map((date, i) => {
          const dateKey = formatDateKey(date);
          const isToday = dateKey === today;
          const isSunday = i === 6;
          const dayAppts = appointments.filter((a) => a.scheduledDate === dateKey);
          return (
            <div
              key={`dayhead-${dateKey}`}
              className={`px-3 py-3 border-r border-border last:border-r-0 ${
                isToday ? 'bg-primary/5' : isSunday ? 'bg-muted/30' : ''
              }`}
            >
              <p className={`text-xs font-600 uppercase tracking-wide ${isSunday ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                {days[i]}
              </p>
              <p className={`text-lg font-700 mt-0.5 tabular-nums ${isToday ? 'text-primary' : 'text-foreground'}`}>
                {date.getDate()}
              </p>
              {dayAppts.length > 0 && (
                <span className="text-xs text-muted-foreground">{dayAppts.length} job{dayAppts.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Appointment cells */}
      <div className="grid grid-cols-7 min-h-[400px]">
        {weekDates.map((date, i) => {
          const dateKey = formatDateKey(date);
          const isToday = dateKey === today;
          const isSunday = i === 6;
          const dayAppts = appointments
            .filter((a) => a.scheduledDate === dateKey)
            .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));

          return (
            <div
              key={`daycell-${dateKey}`}
              className={`border-r border-border last:border-r-0 p-2 space-y-1.5 ${
                isToday ? 'bg-primary/5' : isSunday ? 'bg-muted/20' : ''
              }`}
            >
              {dayAppts.map((appt) => (
                <div
                  key={appt.id}
                  className="bg-card border border-border rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow text-xs group"
                >
                  <div className="flex items-center gap-1 mb-1">
                    <Clock size={10} className="text-muted-foreground shrink-0" />
                    <span className="font-600 text-primary tabular-nums">{appt.scheduledTime}</span>
                    {appt.rebookingFee > 0 && (
                      <span className="ml-auto text-danger font-600">+₱{appt.rebookingFee}</span>
                    )}
                  </div>
                  <p className="font-600 text-foreground leading-tight truncate">{appt.customerName}</p>
                  <p className="text-muted-foreground font-mono truncate">{appt.customerNumber}</p>
                  <p className="text-muted-foreground truncate mt-0.5">{appt.assignedTechnicianName || 'Unassigned'}</p>
                  <div className="flex gap-0.5 mt-1 flex-wrap">
                    {(['sedi', 'pre', 'mem', 'post'] as const).map((f) => (
                      <span
                        key={`${appt.id}-cal-${f}`}
                        className={`px-1 py-0.5 rounded text-xs font-600 leading-none ${
                          appt.filters[f] ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </span>
                    ))}
                  </div>
                  <div className="mt-1">
                    <Badge variant={statusBadge[appt.status]}>
                      {appt.status.charAt(0).toUpperCase() + appt.status.slice(1)}
                    </Badge>
                  </div>
                </div>
              ))}

              {/* Add button */}
              {!isSunday && (
                <button
                  onClick={() => onCreateForDate(dateKey)}
                  className="w-full py-1.5 rounded border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1 opacity-0 hover:opacity-100 group-hover:opacity-100"
                  style={{ opacity: dayAppts.length === 0 ? 1 : undefined }}
                  aria-label="Add appointment"
                >
                  <Plus size={11} />
                  Add
                </button>
              )}

              {isSunday && dayAppts.length === 0 && (
                <p className="text-xs text-center text-muted-foreground py-4">No Sunday jobs</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}